import { Button, toast } from '@aus-platform/design-system';
import { isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useChangeDevice, useVerifySetup2FA } from 'shared/api';
import { SectionHeader, SetupVerification } from 'shared/components';
import {
  getLockoutErrorMessage,
  TwoFactorErrorMessages,
} from 'shared/constants';
import { ErrorSlugs } from 'shared/enums';
import { CustomDate } from 'shared/utils';
import {
  SETUP_FLOW_CONFIG,
  SETUP_FLOW_MESSAGES,
  TOTP_CELL_COUNT,
} from '../constants';
import { SetupFlowMode } from '../enums';
import type { SetupFlowViewProps } from '../types';

export const SetupFlowView: React.FC<SetupFlowViewProps> = ({
  mode,
  elevatedToken,
  setupData,
  onBack,
  onSuccess,
}) => {
  // States.
  const [otpValue, setOtpValue] = useState('');
  const [error, setError] = useState<string>('');

  // Variables.
  const isChangeDevice = mode === SetupFlowMode.ChangeDevice;
  const { backLabel, description, verifyButtonLabel } = SETUP_FLOW_CONFIG[mode];
  const isFormValid = otpValue.length === TOTP_CELL_COUNT;

  // Apis.
  const {
    mutate: changeDevice,
    isPending: isChangingDevice,
    isError: isChangeDeviceError,
    isSuccess: isChangeDeviceSuccess,
    error: changeDeviceError,
  } = useChangeDevice();

  const {
    mutate: verifySetup,
    data: verificationResponse,
    isSuccess: isVerificationSuccess,
    isPending: isVerifying,
    isError: isVerificationError,
    error: verificationError,
  } = useVerifySetup2FA();

  const isSubmitting = isChangingDevice || isVerifying;

  // Effects.
  useEffect(() => {
    if (!isVerificationError && !isChangeDeviceError) {
      return;
    }

    setOtpValue('');
    const currentError = verificationError || changeDeviceError;
    const isInvalidCode = currentError?.meta?.slug === ErrorSlugs.InvalidCode;
    const isAccountLocked = currentError?.meta?.status_code === 429;

    if (isInvalidCode) {
      setError(TwoFactorErrorMessages.InvalidAuthenticatorCode);
      return;
    }

    if (isAccountLocked) {
      const minutesLeft = new CustomDate(
        currentError?.data?.lockout_expires_at,
      ).getRemainingMinutes();

      setError(getLockoutErrorMessage(minutesLeft ?? 0));
    }
  }, [
    isVerificationError,
    isChangeDeviceError,
    verificationError,
    changeDeviceError,
  ]);

  useEffect(() => {
    if (!isChangeDeviceSuccess && !isVerificationSuccess) {
      return;
    }
    setError('');
    setOtpValue('');

    if (isVerificationSuccess) {
      onSuccess(verificationResponse?.data?.backupCodes);
    } else {
      toast.success(SETUP_FLOW_MESSAGES.ChangeDeviceSuccess);
      onSuccess();
    }
  }, [isChangeDeviceSuccess, isVerificationSuccess]);

  // Handlers.
  const handleSubmit = () => {
    setError('');
    if (isChangeDevice && !isNil(elevatedToken)) {
      changeDevice({ code: otpValue, elevatedToken });
    } else {
      verifySetup({ code: otpValue });
    }
  };

  return (
    <div className="settings-content">
      <div className="settings-content__body setup-flow-view">
        <SectionHeader
          backLabel={backLabel}
          description={description}
          onBack={onBack}
        />

        <SetupVerification
          qrCodeUrl={setupData.qrCodeUrl}
          secretKey={setupData.manualEntryKey}
          otpValue={otpValue}
          onOtpChange={setOtpValue}
          disabled={isSubmitting}
          error={error}
        />
      </div>

      <div className="settings-content__footer">
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid}
          isLoading={isSubmitting}
        >
          {verifyButtonLabel}
        </Button>
      </div>
    </div>
  );
};
